import {
  AUTHORITATIVE_RECALCULATION_ROUTE,
  AUTHORITATIVE_RECALCULATION_SCHEMA_VERSION,
  DM2016_AUTHORITATIVE_ROUTE,
  DM2016_AUTHORITATIVE_SCHEMA_VERSION,
  DM2016_BUNDLE_AUTHORITATIVE_ROUTE,
  DM2016_BUNDLE_AUTHORITATIVE_SCHEMA_VERSION,
  DM2016_CANONICAL_QUOTE_ROUTE,
  DM2016_CANONICAL_QUOTE_SCHEMA_VERSION,
  DM2016_OPERATIONAL_RELEASE_GATE_VERSION,
  canonicalJson,
  signedDm2016CanonicalQuoteSchema,
  signedDurableAttestationReceiptSchema,
  type JsonValue,
  type Dm2016FootnoteContext,
  type Dm2016ReferenceBundleServiceInput,
  type Dm2016ReferenceInterpretationMode,
  type EquoCompensoScreeningInput,
  type HashedDm2016FootnoteAwareReferenceBundleResult,
  type SignedAuthoritativeRecalculation,
  type SignedDm2016BundleAuthoritativeRecalculation,
  type SignedDm2016AuthoritativeRecalculation,
  type SignedDm2016CanonicalQuote,
  type SignedDurableAttestationReceipt,
} from "@rcprofessionale/calc-core";

export interface AuthoritativeRecalculationExpectation {
  readonly caseId: string;
  readonly inputSha256: string;
  readonly resultSha256: string;
  readonly engineVersion: string;
  readonly rulesetVersion: string;
  readonly compensationRounded: string;
}

export type AuthoritativeRecalculationClientResult =
  | {
    readonly status: "server_signed";
    readonly document: SignedAuthoritativeRecalculation;
  }
  | {
    readonly status: "demo_fallback";
    readonly reason: "not_configured" | "network_unavailable";
  };

export interface Dm2016AuthoritativeRecalculationExpectation {
  readonly destinationCode: string;
  readonly serviceCode: string;
  readonly valueV: string;
  readonly inhabitants: string | null;
  readonly multiplier: string;
  readonly planning: {
    readonly level: "general" | "executive";
    readonly valueBasis: "territorial_gdp" | "existing_and_design_volumes" | "farm_gross_saleable_production";
  } | null;
  readonly procurementModel: "non_integrated" | "integrated" | null;
  readonly tenderBaseProject: boolean | null;
  readonly publicWorksConcession: boolean | null;
  readonly footnoteResolverVersion: string;
  readonly interpretationMode: Dm2016ReferenceInterpretationMode;
  readonly inputSha256: string;
  readonly resultSha256: string;
  readonly engineVersion: string;
  readonly rulesetVersion: string;
  readonly compensationRounded: string;
}

export type Dm2016AuthoritativeRecalculationClientResult =
  | {
    readonly status: "server_signed";
    readonly document: SignedDm2016AuthoritativeRecalculation;
  }
  | {
    readonly status: "demo_fallback";
    readonly reason: "not_configured" | "network_unavailable";
  };

export interface Dm2016BundleAuthoritativeRecalculationExpectation {
  readonly destinationCode: string;
  readonly requestServices: readonly Dm2016ReferenceBundleServiceInput[];
  readonly footnoteContext: Dm2016FootnoteContext;
  readonly normalizedServices:
    HashedDm2016FootnoteAwareReferenceBundleResult["normalizedInput"]["services"];
  readonly normalizedFootnoteContext:
    HashedDm2016FootnoteAwareReferenceBundleResult["normalizedInput"]["footnoteContext"];
  readonly footnoteResolution:
    HashedDm2016FootnoteAwareReferenceBundleResult["footnoteResolution"];
  readonly footnoteResolverVersion: string;
  readonly interpretationMode: Dm2016ReferenceInterpretationMode;
  readonly inputSha256: string;
  readonly resultSha256: string;
  readonly engineVersion: string;
  readonly rulesetVersion: string;
  readonly compensationRounded: string;
}

export type Dm2016BundleAuthoritativeRecalculationClientResult =
  | {
    readonly status: "server_signed";
    readonly document: SignedDm2016BundleAuthoritativeRecalculation;
  }
  | {
    readonly status: "demo_fallback";
    readonly reason: "not_configured" | "network_unavailable";
  };

export class AuthoritativeRecalculationClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthoritativeRecalculationClientError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function readErrorCode(value: unknown): string | null {
  if (!isRecord(value) || !isRecord(value.error)) return null;
  return typeof value.error.code === "string" ? value.error.code : null;
}

function validateSignedResponse(input: {
  readonly value: unknown;
  readonly requestId: string;
  readonly expected: AuthoritativeRecalculationExpectation;
  readonly now: Date;
}): SignedAuthoritativeRecalculation {
  if (!isRecord(input.value) || !isRecord(input.value.payload) || !isRecord(input.value.signature)) {
    throw new AuthoritativeRecalculationClientError(
      "Il server ha restituito un payload di ricalcolo non valido.",
    );
  }
  const payload = input.value.payload;
  const signature = input.value.signature;
  if (!isRecord(payload.calculation) || !isRecord(payload.release)) {
    throw new AuthoritativeRecalculationClientError(
      "Il risultato server non contiene il calcolo atteso.",
    );
  }
  const calculation = payload.calculation;
  if (!isRecord(calculation.totals)) {
    throw new AuthoritativeRecalculationClientError(
      "Il totale del ricalcolo server non è disponibile.",
    );
  }

  const issuedAt = typeof payload.issuedAt === "string"
    ? Date.parse(payload.issuedAt)
    : Number.NaN;
  const expiresAt = typeof payload.expiresAt === "string"
    ? Date.parse(payload.expiresAt)
    : Number.NaN;
  const now = input.now.getTime();
  const matches =
    payload.schemaVersion === AUTHORITATIVE_RECALCULATION_SCHEMA_VERSION
    && payload.requestId === input.requestId
    && payload.route === AUTHORITATIVE_RECALCULATION_ROUTE
    && payload.caseId === input.expected.caseId
    && payload.status === "recalculated_demo"
    && payload.professionalUseAllowed === false
    && payload.release.calculationEnabled === false
    && calculation.inputSha256 === input.expected.inputSha256
    && calculation.resultSha256 === input.expected.resultSha256
    && calculation.engineVersion === input.expected.engineVersion
    && calculation.rulesetVersion === input.expected.rulesetVersion
    && calculation.totals.compensationRounded === input.expected.compensationRounded
    && signature.algorithm === "HMAC-SHA256"
    && typeof signature.keyId === "string"
    && /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/.test(signature.keyId)
    && typeof signature.value === "string"
    && /^[a-f0-9]{64}$/.test(signature.value)
    && !Number.isNaN(issuedAt)
    && !Number.isNaN(expiresAt)
    && now >= issuedAt
    && now <= expiresAt;
  if (!matches) {
    throw new AuthoritativeRecalculationClientError(
      "Il ricalcolo server non coincide con il calcolo mostrato: PDF bloccato.",
    );
  }
  return input.value as unknown as SignedAuthoritativeRecalculation;
}

export async function requestAuthoritativeRecalculation(input: {
  readonly expected: AuthoritativeRecalculationExpectation;
  readonly endpoint?: string;
  readonly requestId?: string;
  readonly fetcher?: typeof fetch;
  readonly now?: Date;
}): Promise<AuthoritativeRecalculationClientResult> {
  const fetcher = input.fetcher ?? fetch;
  const requestId = input.requestId ?? globalThis.crypto.randomUUID();
  let response: Response;
  try {
    response = await fetcher(input.endpoint ?? "/api/recalculate", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        schemaVersion: AUTHORITATIVE_RECALCULATION_SCHEMA_VERSION,
        requestId,
        route: AUTHORITATIVE_RECALCULATION_ROUTE,
        caseId: input.expected.caseId,
        expectedInputSha256: input.expected.inputSha256,
      }),
    });
  } catch {
    return { status: "demo_fallback", reason: "network_unavailable" };
  }

  let body: unknown;
  try {
    body = await response.json() as unknown;
  } catch {
    throw new AuthoritativeRecalculationClientError(
      "La risposta del ricalcolo server non è leggibile.",
    );
  }
  if (
    response.status === 503
    && readErrorCode(body) === "AUTHORITATIVE_RECALCULATION_NOT_CONFIGURED"
  ) {
    return { status: "demo_fallback", reason: "not_configured" };
  }
  if (!response.ok) {
    throw new AuthoritativeRecalculationClientError(
      `Ricalcolo server rifiutato (${readErrorCode(body) ?? response.status}).`,
    );
  }
  return {
    status: "server_signed",
    document: validateSignedResponse({
      value: body,
      requestId,
      expected: input.expected,
      now: input.now ?? new Date(),
    }),
  };
}

function validateDm2016SignedResponse(input: {
  readonly value: unknown;
  readonly requestId: string;
  readonly expected: Dm2016AuthoritativeRecalculationExpectation;
  readonly now: Date;
}): SignedDm2016AuthoritativeRecalculation {
  if (
    !isRecord(input.value)
    || !isRecord(input.value.payload)
    || !isRecord(input.value.signature)
  ) {
    throw new AuthoritativeRecalculationClientError(
      "Il server ha restituito un payload D.M. 2016 non valido.",
    );
  }
  const payload = input.value.payload;
  const signature = input.value.signature;
  if (
    !isRecord(payload.calculation)
    || !isRecord(payload.normalizedInput)
    || !isRecord(payload.release)
    || !isRecord(payload.trace)
  ) {
    throw new AuthoritativeRecalculationClientError(
      "Il risultato server D.M. 2016 è incompleto.",
    );
  }
  const calculation = payload.calculation;
  const normalizedInput = payload.normalizedInput;
  const footnoteContext = normalizedInput.footnoteContext;
  const normalizedPlanning = isRecord(footnoteContext)
    ? footnoteContext.planning
    : undefined;
  const planningMatches = input.expected.planning === null
    ? normalizedPlanning === null
    : isRecord(normalizedPlanning)
      && normalizedPlanning.level === input.expected.planning.level
      && normalizedPlanning.valueBasis === input.expected.planning.valueBasis;
  const proceduralContextMatches = isRecord(footnoteContext)
    && footnoteContext.procurementModel === input.expected.procurementModel
    && footnoteContext.tenderBaseProject === input.expected.tenderBaseProject
    && footnoteContext.publicWorksConcession === input.expected.publicWorksConcession;
  const issuedAt = typeof payload.issuedAt === "string"
    ? Date.parse(payload.issuedAt)
    : Number.NaN;
  const expiresAt = typeof payload.expiresAt === "string"
    ? Date.parse(payload.expiresAt)
    : Number.NaN;
  const now = input.now.getTime();
  const matches =
    payload.schemaVersion === DM2016_AUTHORITATIVE_SCHEMA_VERSION
    && payload.requestId === input.requestId
    && payload.route === DM2016_AUTHORITATIVE_ROUTE
    && payload.status === "recalculated_dm2016_lab"
    && payload.professionalUseAllowed === true
    && payload.certifiedEquoCompenso === false
    && payload.release.calculationEnabled === true
    && payload.release.certificationEnabled === false
    && payload.release.gateVersion === DM2016_OPERATIONAL_RELEASE_GATE_VERSION
    && normalizedInput.destinationCode === input.expected.destinationCode
    && normalizedInput.serviceCode === input.expected.serviceCode
    && normalizedInput.valueV === input.expected.valueV
    && normalizedInput.inhabitants === input.expected.inhabitants
    && normalizedInput.multiplier === input.expected.multiplier
    && normalizedInput.footnoteResolverVersion === input.expected.footnoteResolverVersion
    && planningMatches
    && proceduralContextMatches
    && normalizedInput.interpretationMode === input.expected.interpretationMode
    && calculation.inputSha256 === input.expected.inputSha256
    && calculation.resultSha256 === input.expected.resultSha256
    && calculation.engineVersion === input.expected.engineVersion
    && calculation.rulesetVersion === input.expected.rulesetVersion
    && calculation.compensationRounded === input.expected.compensationRounded
    && signature.algorithm === "HMAC-SHA256"
    && typeof signature.keyId === "string"
    && /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/.test(signature.keyId)
    && typeof signature.value === "string"
    && /^[a-f0-9]{64}$/.test(signature.value)
    && !Number.isNaN(issuedAt)
    && !Number.isNaN(expiresAt)
    && now >= issuedAt
    && now <= expiresAt;
  if (!matches) {
    throw new AuthoritativeRecalculationClientError(
      "Il ricalcolo server D.M. 2016 non coincide con quello mostrato: PDF bloccato.",
    );
  }
  return input.value as unknown as SignedDm2016AuthoritativeRecalculation;
}

export async function requestDm2016AuthoritativeRecalculation(input: {
  readonly expected: Dm2016AuthoritativeRecalculationExpectation;
  readonly endpoint?: string;
  readonly requestId?: string;
  readonly fetcher?: typeof fetch;
  readonly now?: Date;
}): Promise<Dm2016AuthoritativeRecalculationClientResult> {
  const fetcher = input.fetcher ?? fetch;
  const requestId = input.requestId ?? globalThis.crypto.randomUUID();
  let response: Response;
  try {
    response = await fetcher(input.endpoint ?? "/api/recalculate", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        schemaVersion: DM2016_AUTHORITATIVE_SCHEMA_VERSION,
        requestId,
        route: DM2016_AUTHORITATIVE_ROUTE,
        destinationCode: input.expected.destinationCode,
        serviceCode: input.expected.serviceCode,
        valueV: input.expected.valueV,
        ...(input.expected.inhabitants === null
          ? {}
          : { inhabitants: input.expected.inhabitants }),
        multiplier: input.expected.multiplier,
        ...(input.expected.planning === null
          ? {}
          : { planning: input.expected.planning }),
        ...(input.expected.procurementModel === null
          ? {}
          : { procurementModel: input.expected.procurementModel }),
        ...(input.expected.tenderBaseProject === null
          ? {}
          : { tenderBaseProject: input.expected.tenderBaseProject }),
        ...(input.expected.publicWorksConcession === null
          ? {}
          : { publicWorksConcession: input.expected.publicWorksConcession }),
        interpretationMode: input.expected.interpretationMode,
        expectedInputSha256: input.expected.inputSha256,
        expectedResultSha256: input.expected.resultSha256,
      }),
    });
  } catch {
    return { status: "demo_fallback", reason: "network_unavailable" };
  }

  let body: unknown;
  try {
    body = await response.json() as unknown;
  } catch {
    throw new AuthoritativeRecalculationClientError(
      "La risposta del ricalcolo server D.M. 2016 non è leggibile.",
    );
  }
  if (
    response.status === 503
    && readErrorCode(body) === "AUTHORITATIVE_RECALCULATION_NOT_CONFIGURED"
  ) {
    return { status: "demo_fallback", reason: "not_configured" };
  }
  if (!response.ok) {
    throw new AuthoritativeRecalculationClientError(
      `Ricalcolo server D.M. 2016 rifiutato (${readErrorCode(body) ?? response.status}).`,
    );
  }
  return {
    status: "server_signed",
    document: validateDm2016SignedResponse({
      value: body,
      requestId,
      expected: input.expected,
      now: input.now ?? new Date(),
    }),
  };
}

function validateDm2016BundleSignedResponse(input: {
  readonly value: unknown;
  readonly requestId: string;
  readonly expected: Dm2016BundleAuthoritativeRecalculationExpectation;
  readonly now: Date;
}): SignedDm2016BundleAuthoritativeRecalculation {
  if (
    !isRecord(input.value)
    || !isRecord(input.value.payload)
    || !isRecord(input.value.signature)
  ) {
    throw new AuthoritativeRecalculationClientError(
      "Il server ha restituito un payload multi-prestazione D.M. 2016 non valido.",
    );
  }
  const payload = input.value.payload;
  const signature = input.value.signature;
  if (
    !isRecord(payload.calculation)
    || !isRecord(payload.normalizedInput)
    || !isRecord(payload.release)
    || !isRecord(payload.footnoteResolution)
    || !Array.isArray(payload.traces)
    || !Array.isArray(payload.normalizedInput.services)
  ) {
    throw new AuthoritativeRecalculationClientError(
      "Il risultato server multi-prestazione D.M. 2016 è incompleto.",
    );
  }
  const calculation = payload.calculation;
  const normalizedInput = payload.normalizedInput;
  const issuedAt = typeof payload.issuedAt === "string"
    ? Date.parse(payload.issuedAt)
    : Number.NaN;
  const expiresAt = typeof payload.expiresAt === "string"
    ? Date.parse(payload.expiresAt)
    : Number.NaN;
  const now = input.now.getTime();
  const normalizedServicesMatch = canonicalJson(
    normalizedInput.services as JsonValue,
  ) === canonicalJson(input.expected.normalizedServices as unknown as JsonValue);
  const normalizedContextMatches = canonicalJson(
    normalizedInput.footnoteContext as JsonValue,
  ) === canonicalJson(
    input.expected.normalizedFootnoteContext as unknown as JsonValue,
  );
  const footnoteResolutionMatches = canonicalJson(
    payload.footnoteResolution as unknown as JsonValue,
  ) === canonicalJson(input.expected.footnoteResolution as unknown as JsonValue);
  const tracesMatch = payload.traces.every((trace, index) => {
    if (!isRecord(trace) || !isRecord(trace.resolution) || !isRecord(trace.calculation)) {
      return false;
    }
    const expectedService = input.expected.normalizedServices[index];
    return expectedService !== undefined
      && trace.resolution.serviceCode === expectedService.serviceCode
      && trace.calculation.serviceCode === expectedService.serviceCode
      && trace.calculation.valueV === expectedService.valueV
      && trace.calculation.multiplier === expectedService.multiplier;
  });
  const matches =
    payload.schemaVersion === DM2016_BUNDLE_AUTHORITATIVE_SCHEMA_VERSION
    && payload.requestId === input.requestId
    && payload.route === DM2016_BUNDLE_AUTHORITATIVE_ROUTE
    && payload.status === "recalculated_dm2016_bundle_lab"
    && payload.professionalUseAllowed === true
    && payload.certifiedEquoCompenso === false
    && payload.release.calculationEnabled === true
    && payload.release.certificationEnabled === false
    && payload.release.gateVersion === DM2016_OPERATIONAL_RELEASE_GATE_VERSION
    && normalizedInput.destinationCode === input.expected.destinationCode
    && normalizedServicesMatch
    && normalizedContextMatches
    && footnoteResolutionMatches
    && normalizedInput.footnoteResolverVersion === input.expected.footnoteResolverVersion
    && normalizedInput.interpretationMode === input.expected.interpretationMode
    && payload.traces.length === input.expected.normalizedServices.length
    && tracesMatch
    && calculation.inputSha256 === input.expected.inputSha256
    && calculation.resultSha256 === input.expected.resultSha256
    && calculation.engineVersion === input.expected.engineVersion
    && calculation.rulesetVersion === input.expected.rulesetVersion
    && calculation.compensationRounded === input.expected.compensationRounded
    && signature.algorithm === "HMAC-SHA256"
    && typeof signature.keyId === "string"
    && /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/.test(signature.keyId)
    && typeof signature.value === "string"
    && /^[a-f0-9]{64}$/.test(signature.value)
    && !Number.isNaN(issuedAt)
    && !Number.isNaN(expiresAt)
    && now >= issuedAt
    && now <= expiresAt;
  if (!matches) {
    throw new AuthoritativeRecalculationClientError(
      "Il ricalcolo server multi-prestazione D.M. 2016 non coincide con quello mostrato: PDF bloccato.",
    );
  }
  return input.value as unknown as SignedDm2016BundleAuthoritativeRecalculation;
}

export async function requestDm2016BundleAuthoritativeRecalculation(input: {
  readonly expected: Dm2016BundleAuthoritativeRecalculationExpectation;
  readonly endpoint?: string;
  readonly requestId?: string;
  readonly fetcher?: typeof fetch;
  readonly now?: Date;
}): Promise<Dm2016BundleAuthoritativeRecalculationClientResult> {
  const fetcher = input.fetcher ?? fetch;
  const requestId = input.requestId ?? globalThis.crypto.randomUUID();
  let response: Response;
  try {
    response = await fetcher(input.endpoint ?? "/api/recalculate", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        schemaVersion: DM2016_BUNDLE_AUTHORITATIVE_SCHEMA_VERSION,
        requestId,
        route: DM2016_BUNDLE_AUTHORITATIVE_ROUTE,
        destinationCode: input.expected.destinationCode,
        services: input.expected.requestServices,
        footnoteContext: input.expected.footnoteContext,
        interpretationMode: input.expected.interpretationMode,
        expectedInputSha256: input.expected.inputSha256,
        expectedResultSha256: input.expected.resultSha256,
      }),
    });
  } catch {
    return { status: "demo_fallback", reason: "network_unavailable" };
  }

  let body: unknown;
  try {
    body = await response.json() as unknown;
  } catch {
    throw new AuthoritativeRecalculationClientError(
      "La risposta del ricalcolo server multi-prestazione D.M. 2016 non è leggibile.",
    );
  }
  if (
    response.status === 503
    && readErrorCode(body) === "AUTHORITATIVE_RECALCULATION_NOT_CONFIGURED"
  ) {
    return { status: "demo_fallback", reason: "not_configured" };
  }
  if (!response.ok) {
    throw new AuthoritativeRecalculationClientError(
      `Ricalcolo server multi-prestazione D.M. 2016 rifiutato (${readErrorCode(body) ?? response.status}).`,
    );
  }
  return {
    status: "server_signed",
    document: validateDm2016BundleSignedResponse({
      value: body,
      requestId,
      expected: input.expected,
      now: input.now ?? new Date(),
    }),
  };
}

export interface Dm2016CanonicalQuoteEconomicInput {
  readonly worksValueForExpenseLimit: string;
  readonly bimMandatory: boolean;
  readonly expensePercentage: string;
  readonly reductionPercentage: string;
  readonly socialSecurityPercentage: string;
  readonly vatPercentage: string;
  readonly additionalItems: readonly {
    readonly itemId: string;
    readonly label: string;
    readonly amount: string;
  }[];
}

export interface Dm2016QuoteVerificationReceipt {
  readonly valid: true;
  readonly verificationKind: "live_hmac_plus_durable_attestation";
  readonly integrityValid: true;
  readonly attestationValid: true;
  readonly currentVerificationWindow: true;
  readonly historicallyAuthentic: true;
  readonly quoteSha256: string;
  readonly resultSha256: string;
  readonly keyId: string;
  readonly verifiedAt: string;
  readonly expiresAt: string;
  readonly signedDurableAttestationReceipt: SignedDurableAttestationReceipt;
}

const serverVerifiedReceiptBrand: unique symbol = Symbol("server-verified-dm2016-receipt");
const verifiedReceiptDocuments = new WeakMap<object, SignedDm2016CanonicalQuote>();
export type ServerVerifiedDm2016QuoteReceipt = Dm2016QuoteVerificationReceipt & {
  readonly [serverVerifiedReceiptBrand]: true;
};

export function isServerVerifiedDm2016QuoteReceipt(
  receipt: Dm2016QuoteVerificationReceipt,
): receipt is ServerVerifiedDm2016QuoteReceipt {
  return (receipt as Partial<ServerVerifiedDm2016QuoteReceipt>)[serverVerifiedReceiptBrand] === true;
}

export function isServerVerifiedReceiptForDocument(
  receipt: Dm2016QuoteVerificationReceipt,
  document: SignedDm2016CanonicalQuote,
): receipt is ServerVerifiedDm2016QuoteReceipt {
  return isServerVerifiedDm2016QuoteReceipt(receipt)
    && verifiedReceiptDocuments.get(receipt) === document;
}

function freezeDeep<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach((child) => freezeDeep(child));
  }
  return value;
}

export type Dm2016CanonicalQuoteClientResult =
  | {
    readonly status: "server_verified";
    readonly document: SignedDm2016CanonicalQuote;
    readonly receipt: ServerVerifiedDm2016QuoteReceipt;
  }
  | {
    readonly status: "facsimile_only";
    readonly reason: "not_configured" | "network_unavailable" | "verification_unavailable" | "attestation_not_configured";
  };

function calculationRequest(
  expected: Dm2016AuthoritativeRecalculationExpectation | Dm2016BundleAuthoritativeRecalculationExpectation,
  requestId: string,
): { readonly kind: "single" | "bundle"; readonly request: Record<string, unknown> } {
  if ("serviceCode" in expected) {
    return {
      kind: "single",
      request: {
        schemaVersion: DM2016_AUTHORITATIVE_SCHEMA_VERSION,
        requestId: `${requestId}-calc`,
        route: DM2016_AUTHORITATIVE_ROUTE,
        destinationCode: expected.destinationCode,
        serviceCode: expected.serviceCode,
        valueV: expected.valueV,
        ...(expected.inhabitants === null ? {} : { inhabitants: expected.inhabitants }),
        multiplier: expected.multiplier,
        ...(expected.planning === null ? {} : { planning: expected.planning }),
        ...(expected.procurementModel === null ? {} : { procurementModel: expected.procurementModel }),
        ...(expected.tenderBaseProject === null ? {} : { tenderBaseProject: expected.tenderBaseProject }),
        ...(expected.publicWorksConcession === null ? {} : { publicWorksConcession: expected.publicWorksConcession }),
        interpretationMode: expected.interpretationMode,
        expectedInputSha256: expected.inputSha256,
        expectedResultSha256: expected.resultSha256,
      },
    };
  }
  return {
    kind: "bundle",
    request: {
      schemaVersion: DM2016_BUNDLE_AUTHORITATIVE_SCHEMA_VERSION,
      requestId: `${requestId}-calc`,
      route: DM2016_BUNDLE_AUTHORITATIVE_ROUTE,
      destinationCode: expected.destinationCode,
      services: expected.requestServices,
      footnoteContext: expected.footnoteContext,
      interpretationMode: expected.interpretationMode,
      expectedInputSha256: expected.inputSha256,
      expectedResultSha256: expected.resultSha256,
    },
  };
}

export async function requestAndVerifyDm2016CanonicalQuote(input: {
  readonly expected: Dm2016AuthoritativeRecalculationExpectation | Dm2016BundleAuthoritativeRecalculationExpectation;
  readonly screening: EquoCompensoScreeningInput;
  readonly economicInput: Dm2016CanonicalQuoteEconomicInput;
  readonly endpoint?: string;
  readonly verificationEndpoint?: string;
  readonly requestId?: string;
  readonly fetcher?: typeof fetch;
}): Promise<Dm2016CanonicalQuoteClientResult> {
  const fetcher = input.fetcher ?? fetch;
  const requestId = input.requestId ?? globalThis.crypto.randomUUID();
  let response: Response;
  try {
    response = await fetcher(input.endpoint ?? "/api/recalculate", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        schemaVersion: DM2016_CANONICAL_QUOTE_SCHEMA_VERSION,
        requestId,
        route: DM2016_CANONICAL_QUOTE_ROUTE,
        screening: input.screening,
        calculation: calculationRequest(input.expected, requestId),
        economicInput: input.economicInput,
      }),
    });
  } catch {
    return { status: "facsimile_only", reason: "network_unavailable" };
  }
  let body: unknown;
  try {
    body = await response.json() as unknown;
  } catch {
    throw new AuthoritativeRecalculationClientError("La risposta canonical quote non è leggibile.");
  }
  if (response.status === 503 && readErrorCode(body) === "AUTHORITATIVE_RECALCULATION_NOT_CONFIGURED") {
    return { status: "facsimile_only", reason: "not_configured" };
  }
  const parsedSignedQuote = signedDm2016CanonicalQuoteSchema.safeParse(body);
  if (!response.ok || !parsedSignedQuote.success) {
    throw new AuthoritativeRecalculationClientError(
      `Canonical quote rifiutato (${readErrorCode(body) ?? response.status}).`,
    );
  }
  const document = parsedSignedQuote.data as unknown as SignedDm2016CanonicalQuote;
  const payload = document.payload;
  const calculation = payload.calculation;
  if (
    payload.schemaVersion !== DM2016_CANONICAL_QUOTE_SCHEMA_VERSION
    || payload.requestId !== requestId
    || payload.route !== DM2016_CANONICAL_QUOTE_ROUTE
    || payload.professionalUseAllowed !== true
    || payload.legalCertification !== false
    || !isRecord(calculation)
    || !isRecord(calculation.calculation)
    || calculation.calculation.inputSha256 !== input.expected.inputSha256
    || calculation.calculation.resultSha256 !== input.expected.resultSha256
    || calculation.calculation.compensationRounded !== input.expected.compensationRounded
  ) {
    throw new AuthoritativeRecalculationClientError("Canonical quote non coincide con il calcolo locale: PDF bloccato.");
  }
  let verificationResponse: Response;
  try {
    verificationResponse = await fetcher(input.verificationEndpoint ?? "/api/verify-dm2016-quote", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(document),
    });
  } catch {
    return { status: "facsimile_only", reason: "verification_unavailable" };
  }
  let verification: unknown;
  try {
    verification = await verificationResponse.json() as unknown;
  } catch {
    return { status: "facsimile_only", reason: "verification_unavailable" };
  }
  if (
    verificationResponse.status === 503
    && readErrorCode(verification) === "DURABLE_ATTESTATION_NOT_CONFIGURED"
  ) {
    return { status: "facsimile_only", reason: "attestation_not_configured" };
  }
  const durableReceipt = isRecord(verification)
    ? signedDurableAttestationReceiptSchema.safeParse(verification.signedDurableAttestationReceipt)
    : null;
  if (
    !verificationResponse.ok
    || !isRecord(verification)
    || verification.valid !== true
    || verification.verificationKind !== "live_hmac_plus_durable_attestation"
    || verification.integrityValid !== true
    || verification.attestationValid !== true
    || verification.currentVerificationWindow !== true
    || verification.historicallyAuthentic !== true
    || verification.quoteSha256 !== document.payload.hashes.quoteSha256
    || verification.resultSha256 !== document.payload.hashes.resultSha256
    || verification.keyId !== document.signature.keyId
    || typeof verification.verifiedAt !== "string"
    || typeof verification.expiresAt !== "string"
    || durableReceipt === null
    || !durableReceipt.success
    || durableReceipt.data.payload.quoteSha256 !== document.payload.hashes.quoteSha256
    || durableReceipt.data.payload.resultSha256 !== document.payload.hashes.resultSha256
    || durableReceipt.data.payload.calculationResultSha256 !== document.payload.hashes.calculationResultSha256
    || durableReceipt.data.payload.originatingHmacKeyId !== document.signature.keyId
    || durableReceipt.data.payload.quoteIssuedAt !== document.payload.issuedAt
    || durableReceipt.data.payload.quoteExpiresAt !== document.payload.expiresAt
    || durableReceipt.data.payload.serverVerifiedAt !== verification.verifiedAt
    || durableReceipt.data.payload.releaseId !== document.payload.versions.releaseId
    || durableReceipt.data.payload.releaseVersion !== document.payload.versions.releaseVersion
    || durableReceipt.data.payload.rulesetVersion !== document.payload.versions.rulesetVersion
    || durableReceipt.data.payload.engineVersion !== document.payload.versions.engineVersion
    || durableReceipt.data.payload.quoteSchemaVersion !== document.payload.versions.quoteSchemaVersion
    || durableReceipt.data.payload.interpretationMode !== document.payload.versions.interpretationMode
    || durableReceipt.data.payload.professionalUseAllowed !== true
    || durableReceipt.data.payload.legalCertification !== false
  ) {
    throw new AuthoritativeRecalculationClientError("La verifica crittografica server del preventivo è fallita.");
  }
  const frozenDocument = freezeDeep(document) as SignedDm2016CanonicalQuote;
  const brandedReceipt = Object.freeze({
    ...(verification as unknown as Omit<Dm2016QuoteVerificationReceipt, "signedDurableAttestationReceipt">),
    signedDurableAttestationReceipt: durableReceipt.data as unknown as SignedDurableAttestationReceipt,
    [serverVerifiedReceiptBrand]: true as const,
  });
  verifiedReceiptDocuments.set(brandedReceipt, frozenDocument);
  return {
    status: "server_verified",
    document: frozenDocument,
    receipt: brandedReceipt,
  };
}
